import React, { memo } from 'react';

import { useRouter } from 'next/compat/router';

import { ResourceType, ResourcePermissions } from '@repo/api-client';
import { isMobileOperatingSystem } from '@repo/detection';
import {
  useUserStore,
  userStoreSelector,
} from '@repo/editor/src/stores/userStore';
import { PermissionsCheck } from '@repo/permissions';
import { UpgradeButton, UpgradeCard } from '@repo/subscription/client';
import { themeStyle } from '@repo/ui/theming';
import IconGift from '@repo/ui-icons/IconGift';
import { H6 } from '@repo/ui-kit/components/core/Typography';

import Logo from 'components/Logo/Logo';
import { WorkspaceOrTeamSelector } from 'components/WorkspaceOrTeamSelector';
import {
  useWorkspaceMenuReworkEnabled,
  useWorkspacesFeatureEnabled,
} from 'global/growthbookUtils';
import { useUrlWithWorkspacePrefix } from 'global/utils/workspace/useUrlWithWorkspacePrefix';
import { bannerStoreSelector, useBannerStore } from 'stores/bannerStore';

import { globalMenuItems, getMainNavigationMenu } from './config';
import { NavigationMenuButton } from './NavigationMenuButton';
import { NavigationMenuItem } from './NavigationMenuItem';
import { useSidebar } from './SidebarProvider';
import {
  NavigationMenu,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarSection,
  SidebarWrapper,
} from './styles';
import { SupportPopover } from './SupportPopover';

export const Sidebar: React.FC = memo(() => {
  const user = useUserStore(userStoreSelector.user);
  const { setIsSidebarOpened } = useSidebar();
  const isMobile = isMobileOperatingSystem();
  const router = useRouter();
  const hasBanner = useBannerStore(bannerStoreSelector.isVisible);

  const workspaceMenuReworkEnabled = useWorkspaceMenuReworkEnabled();
  const homePathWithWorkspace = useUrlWithWorkspacePrefix({ href: '/' });
  const workspacesEnabled = useWorkspacesFeatureEnabled();
  const homePath =
    (!workspaceMenuReworkEnabled && homePathWithWorkspace) || '/';

  if (!user) {
    return;
  }
  // temporarily hide tools in mobile until we fix it
  // TODO (https://app.clickup.com/t/86c21uj4k)
  const filteredMenu = getMainNavigationMenu(
    workspaceMenuReworkEnabled,
    workspacesEnabled
  ).map((section) => ({
    ...section,
    items: section.items.filter((item) => item.label !== 'Tools' || !isMobile),
  }));

  const handleEarnRewards = () => {
    router?.push('/user/settings/referral', undefined, { shallow: false });
  };

  return (
    <SidebarWrapper hasBanner={hasBanner}>
      {/* If the workspaceMenuReworkEnabled toggle is enabled, render the header and global menu items in its
      own SidebarSection. otherwise, put the header and other existing items all
      in the same sidebar section */}

      {workspaceMenuReworkEnabled && (
        <SidebarSection>
          <SidebarHeader>
            <Logo height="22px" href={homePath} width="56px" />
          </SidebarHeader>

          <SidebarContent>
            <NavigationMenu>
              {globalMenuItems.map((item) => (
                <NavigationMenuItem key={item.label} {...item} />
              ))}
            </NavigationMenu>
          </SidebarContent>
        </SidebarSection>
      )}
      <SidebarSection>
        {!workspaceMenuReworkEnabled && (
          <SidebarHeader>
            <Logo height="22px" href={homePath} width="56px" />
          </SidebarHeader>
        )}
        <SidebarContent aria-label="Main menu navigation">
          <WorkspaceOrTeamSelector />
          {filteredMenu.map((section, idx) => (
            <PermissionsCheck
              key={idx}
              requires={section.requiredPermission}
              resourceType={ResourceType.workspace}
            >
              <section key={`${section.title || 'section'}-${idx}`}>
                {section.title && (
                  <H6 bold as="h2" margin="var(--spacing-8)">
                    {section.title}
                  </H6>
                )}
                <NavigationMenu>
                  {section.items.map((item) => (
                    <PermissionsCheck
                      key={item.label}
                      requires={
                        item.requiredPermission ||
                        ResourcePermissions.DESIGN_VIEW
                      }
                      resourceType={ResourceType.workspace}
                    >
                      <NavigationMenuItem
                        key={item.label}
                        isCollapseable
                        activeUrls={item.activeUrls}
                        href={item.href}
                        hrefPrefixedByWorkspace={item.hrefPrefixedByWorkspace}
                        icon={item.icon}
                        items={item.items}
                        label={item.label}
                        requiredPermission={item.requiredPermission}
                      />
                    </PermissionsCheck>
                  ))}
                </NavigationMenu>
              </section>
            </PermissionsCheck>
          ))}
        </SidebarContent>

        <SidebarFooter>
          <UpgradeCard>
            <UpgradeButton
              showIcon={false}
              width="100%"
              onAfterUpgradeClick={() => {
                if (isMobile) {
                  setIsSidebarOpened(false);
                }
              }}
            />
          </UpgradeCard>
          {workspacesEnabled && (
            <NavigationMenuButton onClick={handleEarnRewards}>
              <IconGift color={themeStyle.inkMedium} size={16} />
              <H6 as="span" color="secondary">
                Earn Rewards
              </H6>
            </NavigationMenuButton>
          )}
          <SupportPopover />
        </SidebarFooter>
      </SidebarSection>
    </SidebarWrapper>
  );
});

Sidebar.displayName = 'Sidebar';
